import { useEffect, useState } from "react";
import { HandCoins, Mail, Plus, Printer, ReceiptText, RotateCcw, Save, Wallet } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const inr = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const STATUS_STYLES = {
  POSTED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  REVERSED: "bg-rose-50 text-rose-800 border-rose-200",
};

const TABS = [
  ["receipts", "Receipts", HandCoins],
  ["vouchers", "Payment Vouchers", ReceiptText],
  ["outstanding", "Outstanding", Wallet],
];

export default function FreshFinanceReceipts({ accounts = [], access }) {
  const permissions = access?.permissions || [];
  const can = (name) => permissions.includes(name);
  const [tab, setTab] = useState("receipts");
  const [units, setUnits] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/fresh-finance/masters/units", { params: { limit: 5000 } });
        setUnits(res.data.items || []);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const assetAccounts = accounts.filter((a) => a.account_type === "ASSET");
  const expenseAccounts = accounts.filter((a) => a.account_type === "EXPENSE");

  return (
    <section data-testid="fresh-finance-receipts-section">
      <div className="border-b border-gray-200 pb-3">
        <p className="text-xs font-semibold uppercase text-blue-700">Phase C</p>
        <h2 className="mt-1 font-display text-2xl font-bold">
          Receipts, Allocation &amp; Vouchers
        </h2>
      </div>
      <div className="mt-4 flex flex-wrap border-b border-gray-200" data-testid="fresh-finance-receipts-tabs">
        {TABS.map(([id, label, Icon]) => (
          <button
            className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${
              tab === id ? "border-blue-700 text-blue-800" : "border-transparent text-gray-600 hover:text-gray-950"
            }`}
            data-testid={`fresh-finance-receipts-tab-${id}`}
            key={id}
            onClick={() => setTab(id)}
            type="button"
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === "receipts" && (
        <ReceiptsTab accounts={assetAccounts} canManage={can("manage_receipts")} units={units} />
      )}
      {tab === "vouchers" && (
        <VouchersTab
          assetAccounts={assetAccounts}
          canManage={can("manage_vouchers")}
          expenseAccounts={expenseAccounts}
        />
      )}
      {tab === "outstanding" && <OutstandingTab units={units} />}
    </section>
  );
}

function ReceiptsTab({ accounts, canManage, units }) {
  const today = new Date().toISOString().slice(0, 10);
  const empty = {
    unit_id: "",
    date: today,
    amount: "",
    mode: "CASH",
    reference: "",
    deposit_account_id: "",
    narration: "",
  };
  const [form, setForm] = useState(empty);
  const [receipts, setReceipts] = useState([]);
  const [reversing, setReversing] = useState(null);

  const load = async () => {
    const res = await api.get("/fresh-finance/receipts");
    setReceipts(res.data || []);
  };
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      const res = await api.post("/fresh-finance/receipts", {
        ...form,
        amount: Number(form.amount || 0),
      });
      toast.success(
        `Receipt ${res.data.receipt_number} posted — allocated ${inr(res.data.allocated_total)}` +
          (Number(res.data.advance_amount) > 0 ? `, advance ${inr(res.data.advance_amount)}` : ""),
      );
      setForm(empty);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not post receipt");
    }
  };

  const openPdf = async (id) => {
    try {
      const res = await api.get(`/fresh-finance/receipts/${id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Could not open receipt PDF");
    }
  };

  const emailReceipt = async (id) => {
    try {
      const response = await api.post(`/fresh-finance/receipts/${id}/email`);
      toast.success(
        response.data.status === "ALREADY_SENT"
          ? "This receipt was already emailed"
          : "Receipt emailed to the member",
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not email the receipt");
    }
  };

  return (
    <div className="mt-4 space-y-5" data-testid="fresh-finance-receipts">
      <section className="border border-gray-200 bg-white p-4" data-testid="fresh-finance-receipt-form">
        <h3 className="font-display text-sm font-bold">Record a receipt</h3>
        <fieldset className="mt-3 flex flex-wrap items-end gap-3 disabled:opacity-50" disabled={!canManage}>
          <Field label="Unit">
            <select className="h-8 min-w-36 border border-gray-300 bg-white px-2 text-sm"
              data-testid="fresh-finance-receipt-unit" onChange={set("unit_id")} value={form.unit_id}>
              <option value="">Select unit</option>
              {units.map((u) => (
                <option key={u.unit_id} value={u.unit_id}>{u.unit_number}</option>
              ))}
            </select>
          </Field>
          <Field label="Date">
            <Input className="h-8 border-gray-300 text-sm" data-testid="fresh-finance-receipt-date"
              onChange={set("date")} type="date" value={form.date} />
          </Field>
          <Field label="Amount (₹)">
            <Input className="h-8 w-28 border-gray-300 text-sm" data-testid="fresh-finance-receipt-amount"
              onChange={set("amount")} type="number" value={form.amount} />
          </Field>
          <Field label="Mode">
            <select className="h-8 border border-gray-300 bg-white px-2 text-sm"
              data-testid="fresh-finance-receipt-mode" onChange={set("mode")} value={form.mode}>
              {["CASH", "CHEQUE", "BANK_TRANSFER", "UPI"].map((m) => (
                <option key={m} value={m}>{m.replace("_", " ")}</option>
              ))}
            </select>
          </Field>
          <Field label="Reference">
            <Input className="h-8 w-32 border-gray-300 text-sm" data-testid="fresh-finance-receipt-reference"
              onChange={set("reference")} value={form.reference} />
          </Field>
          <Field label="Deposit to">
            <select className="h-8 min-w-36 border border-gray-300 bg-white px-2 text-sm"
              data-testid="fresh-finance-receipt-account" onChange={set("deposit_account_id")}
              value={form.deposit_account_id}>
              <option value="">Cash / Bank account</option>
              {accounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>
              ))}
            </select>
          </Field>
          <Button data-testid="fresh-finance-receipt-save" onClick={save} size="sm" type="button">
            <Save className="h-4 w-4" />
            Post receipt
          </Button>
        </fieldset>
        <p className="mt-2 text-xs text-gray-500">
          The amount is auto-allocated to the unit's oldest unpaid bills first; any excess is held as an advance.
        </p>
      </section>

      <div className="overflow-x-auto border border-gray-200" data-testid="fresh-finance-receipts-table">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2">Receipt No.</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Unit</th>
              <th className="px-3 py-2">Mode</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-right">Allocated</th>
              <th className="px-3 py-2 text-right">Advance</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {receipts.length === 0 && (
              <tr><td className="px-3 py-4 text-gray-500" colSpan={9}>No receipts yet.</td></tr>
            )}
            {receipts.map((r) => (
              <tr key={r.receipt_id} data-testid={`fresh-finance-receipt-row-${r.unit_snapshot?.unit_number}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.receipt_number}</td>
                <td className="px-3 py-2">{r.date}</td>
                <td className="px-3 py-2 font-medium">{r.unit_snapshot?.unit_number}</td>
                <td className="px-3 py-2">{r.mode}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">{inr(r.amount)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{inr(r.allocated_total)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{inr(r.advance_amount)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[r.status]}`}>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <button className="text-xs font-semibold text-blue-700"
                      data-testid={`fresh-finance-receipt-pdf-${r.unit_snapshot?.unit_number}`}
                      onClick={() => openPdf(r.receipt_id)} type="button">PDF</button>
                    {r.status === "POSTED" && canManage && (
                      <button
                        className="text-xs font-semibold text-emerald-700"
                        data-testid={`fresh-finance-receipt-email-${r.unit_snapshot?.unit_number}`}
                        onClick={() => emailReceipt(r.receipt_id)}
                        type="button"
                      >
                        <Mail className="mr-1 inline h-3 w-3" />
                        Email
                      </button>
                    )}
                    {r.status === "POSTED" && canManage && (
                      <button className="text-xs font-semibold text-rose-700"
                        data-testid={`fresh-finance-receipt-reverse-${r.unit_snapshot?.unit_number}`}
                        onClick={() => setReversing(r)} type="button">
                        <RotateCcw className="mr-1 inline h-3 w-3" />Reverse
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {reversing && (
        <ReverseModal
          label={`receipt ${reversing.receipt_number}`}
          detail={`${reversing.unit_snapshot?.unit_number} · ${inr(reversing.amount)}`}
          onClose={() => setReversing(null)}
          onConfirm={async (reason) => {
            await api.post(`/fresh-finance/receipts/${reversing.receipt_id}/reverse`, { reason });
            toast.success("Receipt reversed with a balancing entry");
            setReversing(null);
            load();
          }}
          testid="receipt"
        />
      )}
    </div>
  );
}

function PayeePicker({ value, onChange, canManage }) {
  const [types, setTypes] = useState([]);
  const [payees, setPayees] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", type: "", contact: "" });

  const load = async () => {
    try {
      const res = await api.get("/fresh-finance/payees");
      setTypes(res.data?.types || []);
      setPayees(res.data?.payees || []);
    } catch {
      setTypes([]);
      setPayees([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const saveNew = async () => {
    const name = draft.name.trim();
    if (!name) {
      toast.error("Payee name is required");
      return;
    }
    try {
      const res = await api.post("/fresh-finance/payees", draft);
      toast.success("Payee saved");
      await load();
      onChange(res.data.name);
      setDraft({ name: "", type: "", contact: "" });
      setAdding(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save payee");
    }
  };

  return (
    <Field label="Payee">
      {adding ? (
        <div className="flex flex-wrap items-end gap-2" data-testid="fresh-finance-payee-add">
          <Input
            className="h-8 w-40 border-gray-300 text-sm"
            data-testid="fresh-finance-payee-new-name"
            placeholder="Payee name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <select
            className="h-8 min-w-36 border border-gray-300 bg-white px-2 text-sm"
            data-testid="fresh-finance-payee-new-type"
            value={draft.type}
            onChange={(e) => setDraft({ ...draft, type: e.target.value })}
          >
            <option value="">Payee type</option>
            {types.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <Button type="button" size="sm" data-testid="fresh-finance-payee-new-save" onClick={saveNew}>
            Save
          </Button>
          <Button type="button" size="sm" variant="outline" data-testid="fresh-finance-payee-new-cancel" onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <select
            className="h-8 min-w-40 border border-gray-300 bg-white px-2 text-sm"
            data-testid="fresh-finance-voucher-payee"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select payee</option>
            {payees.length > 0 && (
              <optgroup label="Saved payees">
                {payees.map((p) => (
                  <option key={p.payee_id} value={p.name}>
                    {p.name}{p.type ? ` · ${p.type}` : ""}
                  </option>
                ))}
              </optgroup>
            )}
            {value && !payees.some((p) => p.name === value) && (
              <option value={value}>{value}</option>
            )}
          </select>
          {canManage && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="fresh-finance-payee-add-toggle"
              onClick={() => {
                setDraft({ name: "", type: "", contact: "" });
                setAdding(true);
              }}
            >
              <Plus className="h-4 w-4" /> New
            </Button>
          )}
        </div>
      )}
    </Field>
  );
}

function VouchersTab({ assetAccounts, canManage, expenseAccounts }) {
  const today = new Date().toISOString().slice(0, 10);
  const empty = {
    date: today,
    amount: "",
    payee: "",
    narration: "",
    expense_account_id: "",
    paid_from_account_id: "",
  };
  const [form, setForm] = useState(empty);
  const [vouchers, setVouchers] = useState([]);
  const [reversing, setReversing] = useState(null);

  const load = async () => {
    const res = await api.get("/fresh-finance/vouchers");
    setVouchers(res.data || []);
  };
  useEffect(() => {
    load();
  }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      const res = await api.post("/fresh-finance/vouchers", { ...form, amount: Number(form.amount || 0) });
      toast.success(`Voucher ${res.data.voucher_number} posted`);
      setForm(empty);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not post voucher");
    }
  };

  return (
    <div className="mt-4 space-y-5" data-testid="fresh-finance-vouchers">
      <section className="border border-gray-200 bg-white p-4" data-testid="fresh-finance-voucher-form">
        <h3 className="font-display text-sm font-bold">Record a payment voucher</h3>
        <fieldset className="mt-3 flex flex-wrap items-end gap-3 disabled:opacity-50" disabled={!canManage}>
          <Field label="Date">
            <Input className="h-8 border-gray-300 text-sm" data-testid="fresh-finance-voucher-date"
              onChange={set("date")} type="date" value={form.date} />
          </Field>
          <PayeePicker
            value={form.payee}
            onChange={(name) => setForm({ ...form, payee: name })}
            canManage={canManage}
          />
          <Field label="Amount (₹)">
            <Input className="h-8 w-28 border-gray-300 text-sm" data-testid="fresh-finance-voucher-amount"
              onChange={set("amount")} type="number" value={form.amount} />
          </Field>
          <Field label="Expense (debit)">
            <select className="h-8 min-w-40 border border-gray-300 bg-white px-2 text-sm"
              data-testid="fresh-finance-voucher-expense" onChange={set("expense_account_id")}
              value={form.expense_account_id}>
              <option value="">Expense account</option>
              {expenseAccounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Paid from">
            <select className="h-8 min-w-36 border border-gray-300 bg-white px-2 text-sm"
              data-testid="fresh-finance-voucher-paidfrom" onChange={set("paid_from_account_id")}
              value={form.paid_from_account_id}>
              <option value="">Cash / Bank account</option>
              {assetAccounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>
              ))}
            </select>
          </Field>
          <Button data-testid="fresh-finance-voucher-save" onClick={save} size="sm" type="button">
            <Save className="h-4 w-4" />
            Post voucher
          </Button>
        </fieldset>
      </section>

      <div className="overflow-x-auto border border-gray-200" data-testid="fresh-finance-vouchers-table">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2">Voucher No.</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Payee</th>
              <th className="px-3 py-2">Expense</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {vouchers.length === 0 && (
              <tr><td className="px-3 py-4 text-gray-500" colSpan={7}>No vouchers yet.</td></tr>
            )}
            {vouchers.map((v) => (
              <tr key={v.voucher_id} data-testid={`fresh-finance-voucher-row-${v.voucher_number}`}>
                <td className="px-3 py-2 font-mono text-xs">{v.voucher_number}</td>
                <td className="px-3 py-2">{v.date}</td>
                <td className="px-3 py-2 font-medium">{v.payee}</td>
                <td className="px-3 py-2 text-xs">{v.expense_account_name}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">{inr(v.amount)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[v.status]}`}>{v.status}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  {v.status === "POSTED" && canManage && (
                    <button className="text-xs font-semibold text-rose-700"
                      data-testid={`fresh-finance-voucher-reverse-${v.voucher_number}`}
                      onClick={() => setReversing(v)} type="button">
                      <RotateCcw className="mr-1 inline h-3 w-3" />Reverse
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {reversing && (
        <ReverseModal
          label={`voucher ${reversing.voucher_number}`}
          detail={`${reversing.payee} · ${inr(reversing.amount)}`}
          onClose={() => setReversing(null)}
          onConfirm={async (reason) => {
            await api.post(`/fresh-finance/vouchers/${reversing.voucher_id}/reverse`, { reason });
            toast.success("Voucher reversed with a balancing entry");
            setReversing(null);
            load();
          }}
          testid="voucher"
        />
      )}
    </div>
  );
}

function OutstandingTab({ units }) {
  const [unitId, setUnitId] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!unitId) {
      setRows([]);
      return;
    }
    (async () => {
      const res = await api.get(`/fresh-finance/units/${unitId}/outstanding`);
      setRows(res.data || []);
    })();
  }, [unitId]);

  const total = rows.reduce((sum, r) => sum + Number(r.outstanding || 0), 0);

  return (
    <div className="mt-4 space-y-4" data-testid="fresh-finance-outstanding">
      <section className="flex flex-wrap items-end gap-3 border border-gray-200 bg-white p-4">
        <Field label="Unit">
          <select className="h-8 min-w-40 border border-gray-300 bg-white px-2 text-sm"
            data-testid="fresh-finance-outstanding-unit" onChange={(e) => setUnitId(e.target.value)} value={unitId}>
            <option value="">Select unit</option>
            {units.map((u) => (
              <option key={u.unit_id} value={u.unit_id}>{u.unit_number}</option>
            ))}
          </select>
        </Field>
        {unitId && (
          <span className="ml-auto text-sm font-semibold" data-testid="fresh-finance-outstanding-total">
            Total outstanding: {inr(total)}
          </span>
        )}
      </section>
      {unitId && (
        <div className="overflow-x-auto border border-gray-200" data-testid="fresh-finance-outstanding-table">
          <table className="w-full min-w-[620px] text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2">Bill No.</th>
                <th className="px-3 py-2">Period</th>
                <th className="px-3 py-2 text-right">Grand Total</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2 text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {rows.length === 0 && (
                <tr><td className="px-3 py-4 text-gray-500" colSpan={5}>No finalized bills for this unit.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.bill_id}>
                  <td className="px-3 py-2 font-mono text-xs">{r.bill_number}</td>
                  <td className="px-3 py-2">{r.period}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{inr(r.grand_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{inr(r.paid)}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{inr(r.outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReverseModal({ detail, label, onClose, onConfirm, testid }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!reason.trim()) {
      toast.error("Reversal reason is required");
      return;
    }
    setBusy(true);
    try {
      await onConfirm(reason);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not reverse");
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid={`fresh-finance-${testid}-reverse-modal`}>
      <div className="w-full max-w-md border border-gray-200 bg-white p-5">
        <h3 className="font-display text-lg font-bold">Reverse {label}?</h3>
        <p className="mt-1 text-sm text-gray-600">{detail}</p>
        <p className="mt-2 text-xs text-gray-500">
          A balancing reversal entry will be posted and the record marked REVERSED.
        </p>
        <Input className="mt-3 h-8 border-gray-300 text-sm" data-testid={`fresh-finance-${testid}-reverse-reason`}
          onChange={(e) => setReason(e.target.value)} placeholder="Reason" value={reason} />
        <div className="mt-4 flex justify-end gap-2">
          <Button data-testid={`fresh-finance-${testid}-reverse-cancel`} onClick={onClose} size="sm"
            type="button" variant="outline">Cancel</Button>
          <Button className="bg-rose-600 hover:bg-rose-700" data-testid={`fresh-finance-${testid}-reverse-confirm`}
            disabled={busy} onClick={go} size="sm" type="button">Confirm reversal</Button>
        </div>
      </div>
    </div>
  );
}

function Field({ children, label }) {
  return (
    <label className="grid gap-1 text-xs font-semibold text-gray-600">
      <span>{label}</span>
      {children}
    </label>
  );
}
